import { mapPlayerLook } from '@/lib/api/rewards-hub/mappers';
import type {
  Blockchain,
  BonusBurnPreview,
  Coin,
  DepositAddress,
  InitiateWithdrawResult,
  TopHolder,
  TopHoldersResponse,
  Transaction,
  WithdrawSettings,
} from '@/lib/api/transaction/models';
import type {
  BlockchainDto,
  BonusBurnPreviewDto,
  CoinDto,
  DepositAddressResponseDto,
  InitiateWithdrawResponseDto,
  TopHolderDto,
  TopHoldersResponseDto,
  TransactionDto,
  WithdrawSettingsDto,
} from '@/lib/api/transaction/types';

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

export function mapTransaction(dto: TransactionDto): Transaction {
  return {
    id: dto.i,
    userId: dto.u,
    assetId: dto.a,
    transactionType: dto.t,
    amount: toNumberOrNull(dto.am),
    status: dto.s,
    createdAt: dto.cat,
    externalTransactionId: dto.e,
    fees: dto.f !== undefined ? toNumberOrNull(dto.f) : null,
    outcomeName: dto.on,
    tokenName: dto.tn,
    isPositive: dto.ip,
    eventSlug: dto.es,
    eventImage: dto.ei ?? undefined,
    eventTitle: dto.eq,
    toAddress: dto.ta,
    blockExplorerUrl: dto.bu,
    blockchainName: dto.bn,
  };
}

export function mapCoin(dto: CoinDto): Coin {
  return {
    id: dto.i,
    name: dto.n,
    shortName: dto.sn,
    usdRate: toNumberOrNull(dto.ur),
    usdRateUpdateDatetime: dto.urd,
    decimalPlacesFull: dto.dpf,
    decimalPlacesShort: dto.dps,
    active: dto.ac,
    iconUrl: dto.iu,
    paymentMethodCoin: dto.pmc,
    systemCollateral: dto.sc,
  };
}

export function mapTopHolder(dto: TopHolderDto): TopHolder {
  return {
    userId: dto.u,
    name: dto.n,
    outcomeId: dto.oi,
    side: dto.si,
    quantity: dto.q,
    rank: dto.r,
    look: mapPlayerLook(dto.lk),
  };
}

export function mapTopHoldersResponse(dto: TopHoldersResponseDto): TopHoldersResponse {
  return {
    outcomeId: dto.oi,
    side: dto.si,
    period: dto.p,
    totalCount: dto.t,
    items: (dto.i ?? []).map(mapTopHolder),
  };
}

export function mapBlockchain(dto: BlockchainDto): Blockchain {
  return {
    id: dto.i,
    name: dto.n,
    displayName: dto.n2,
    status: dto.s,
    fee: dto.f,
    feeUsd: dto.fu,
  };
}

export function mapWithdrawSettings(dto: WithdrawSettingsDto): WithdrawSettings {
  return {
    minWithdrawal: toNumberOrNull(dto.m) ?? 0,
    fee: toNumberOrNull(dto.f) ?? 0,
    contractAddress: dto.c ?? undefined,
  };
}

export function mapBonusBurnPreview(dto: BonusBurnPreviewDto): BonusBurnPreview {
  return {
    sharePct: toNumberOrNull(dto.sharePct) ?? 0,
    burnPct: toNumberOrNull(dto.burnPct) ?? 0,
    burnAmount: toNumberOrNull(dto.burnAmount) ?? 0,
    remainingAfter: toNumberOrNull(dto.remainingAfter) ?? 0,
  };
}

export function mapInitiateWithdrawResult(
  dto: InitiateWithdrawResponseDto,
): InitiateWithdrawResult {
  return {
    transactionId: dto.o,
    orderId: dto.o,
    email: dto.e,
  };
}

export function mapDepositAddress(dto: DepositAddressResponseDto): DepositAddress {
  return {
    address: dto.a,
    tag: dto.t,
  };
}
